import { withForm } from '@/lib/form.ts';
import { getDispenserFormOptions } from '@/features/dispensers/components/dispenser-form/config.ts';
import { useStore } from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { typeQueries, typeQueryKeys } from '@/features/types/queries.ts';
import { TypeService } from '@/features/types/services/TypeService.ts';
import { Trans, useLingui } from '@lingui/react/macro';

export const TypeField = withForm({
  ...getDispenserFormOptions(),
  render: function Render({ form }) {
    const { t } = useLingui();
    const queryClient = useQueryClient();

    const producerId = useStore(form.store, (state) => state.values.producerId);

    const typeListQuery = useQuery(typeQueries.list(producerId));

    const createTypeMutation = useMutation({
      mutationFn: TypeService.create,
      onSuccess: (_, variables) =>
        queryClient.invalidateQueries({ queryKey: typeQueryKeys.list(variables.producerId) }),
    });

    const typeChangeListener = () => {
      createTypeMutation.reset();
    };

    return (
      <form.AppField name="typeId" listeners={{ onChange: typeChangeListener }}>
        {(field) => (
          <field.CreatableCombobox
            label={<Trans>Type</Trans>}
            required
            loading={typeListQuery.isLoading}
            canCreateNew={!!producerId}
            noOptionsText={!producerId ? <Trans>Please select a producer first</Trans> : undefined}
            options={typeListQuery.data?.result.map((t) => ({ ...t, id: `${t.id}` })) ?? []}
            getOptionLabel={(option) => option.code}
            getOptionKey={(option) => option.id}
            onCreateNew={async (input) => {
              if (!producerId) {
                throw new Error('Producer must be selected first');
              }
              const result = await createTypeMutation.mutateAsync({
                code: input,
                producerId: producerId,
              });
              return `${result.id}`;
            }}
            errorMessage={
              typeListQuery.isError
                ? t`Couldn't load types for this producer`
                : createTypeMutation.isError
                  ? t`Could not create new type`
                  : undefined
            }
          />
        )}
      </form.AppField>
    );
  },
});
